import React, {Component} from 'react';
import _ from 'lodash';
import units from 'units-css';
import {createContextConsumerHOC} from '@elixir/theme';

export const BreakpointTrackerContext = React.createContext();

export function withBreakpoint (ComponentToWrap) {
    return createContextConsumerHOC({
        component: ComponentToWrap,
        context: BreakpointTrackerContext,
        mapContextToProps: (existingProps, breakpoint) => breakpoint
    });
}

export function renderWithBreakpoint (render) {
    if (__TEST__) {
        return render('desktop');
    }
    return (
        <BreakpointTrackerContext.Consumer>
            {(breakpoint) => render(breakpoint)}
        </BreakpointTrackerContext.Consumer>
    );
}

// Inspired by https://github.com/ehellman/react-breakpoints

export default class BreakpointTracker extends Component {
    static defaultProps = {
        delayMs: 50
    };

    state = {};

    static applyBreakpointOverrides (props, breakpoint) {
        let componentProps = props;

        // apply breakpoint property overrides
        if (breakpoint === 'phone' && (props.phone || props.tablet)) {
            componentProps = Object.assign({}, props, props.tablet, props.phone);
        }
        else if (breakpoint === 'tablet' && props.tablet) {
            componentProps = Object.assign({}, props, props.tablet);
        }

        return componentProps;
    }

    /**
     * Gets devices breakpoints calculated in pixels.
     * If rootElement is not specified all relative units (em, %) rely on document.body.
     *
     * @param {Object} [rootElement] - root DOM element for width calculation of relative units(em, %)
     * @returns {Object}
     */
    static getBreakpoints (rootElement) {
        return {
            phone: BreakpointTracker.getBreakpointPxValue('phone', rootElement) || 768,
            tablet: BreakpointTracker.getBreakpointPxValue('tablet', rootElement) || 1024
        };
    }

    static getBreakpointPxValue (device, rootElement) {
        let element = rootElement || (document && document.body);
        const themeRoot = element.querySelector('.themeRoot');
        if (themeRoot) {
            element = themeRoot;
        }
        const breakpointValue = (typeof getComputedStyle === 'function') && element && device &&
            getComputedStyle(element).getPropertyValue(`--GW-BREAKPOINT-${device.toUpperCase()}`);
        return breakpointValue && units.convert('px', breakpointValue, element);
    }

    componentDidMount () {
        this.breakpoints = BreakpointTracker.getBreakpoints();

        window.addEventListener('resize', this.throttleResize);
        window.addEventListener('orientationchange', this.handleResize);

        this.setState({
            breakpoint: this.getBreakpointFromWidth(window.innerWidth)
        });
    }

    componentWillUnmount () {
        window.removeEventListener('resize', this.throttleResize);
        window.removeEventListener('orientationchange', this.handleResize);
    }

    throttleResize = () => {
        // Note: investigate which method is better? last event only vs every 50 milliseconds
        // last event only (debounce-like)
        // clearTimeout(this.resizeTimer);
        // this.resizeTimer = setTimeout(this.handleResize, 250);

        // every ${props.delayMs} milliseconds (debounce)
        if (!this.resizeTimer) {
            this.resizeTimer = setTimeout(this.handleResize, this.props.delayMs);
        }
    }

    handleResize = () => {
        this.resizeTimer = null; // clear after debounce
        const newBreakpoint = this.getBreakpointFromWidth(window.innerWidth, this.breakpoints);
        if (this.state.breakpoint !== newBreakpoint) {
            this.setState({breakpoint: newBreakpoint});
        }
    }

    getBreakpointFromWidth (width) {
        const breakpoints = this.breakpoints;
        let breakpoint = 'desktop';
        const keys = _.keys(breakpoints);
        _.forEachRight(keys, key => {
            if (width < breakpoints[ key ]) {
                breakpoint = key;
            }
        });
        return breakpoint;
    }

    render () {
        const {breakpoint} = this.state;
        if (!breakpoint) {
            return null;
        }

        return <BreakpointTrackerContext.Provider value={breakpoint}>
            {this.props.children}
        </BreakpointTrackerContext.Provider>;
    }
}
